import { setServiceSetting } from "@springtree/eva-sdk-core-service";
import { NextRouter } from "next/router";
import { createContext, useContext, useEffect, useMemo } from "react";

import { mapLocale } from "~/services/algolia-utils";
import Eva from "~/services/eva";
import { HttpService } from "~/services/http";
import crossCountryUtils from "~/utils/cross-country-utils";

import { KIKOApplicationConfiguration } from "../../global";

const Context = createContext<
  | {
      eva: Eva;
      organizationUnitId: string;
      organizationUnitBackendId: string;
      configuration: KIKOApplicationConfiguration;
      algoliaIndexId: string;
    }
  | undefined
>(undefined);

export default function ConfigurationProvider(props: {
  organizationUnitId: string;
  organizationUnitBackendId: string;
  configuration: KIKOApplicationConfiguration;
  children: React.ReactNode;
  router: NextRouter;
}) {
  const locale = crossCountryUtils.getCurrentLocaleWithCountryUppercase(props.router);
  const eva = useMemo(
    () => new Eva({ organizationUnitId: props.organizationUnitId, locale }),
    [locale, props.organizationUnitId]
  );

  const algoliaIndexId = useMemo(() => {
    return mapLocale(props.configuration?.["App:Ecommerce:AlgoliaIndexPrefix"] ?? props.organizationUnitId, locale);
  }, [locale, props.organizationUnitId, props.configuration]);

  useEffect(() => {
    setServiceSetting("requestedOrganizationUnitID", props.organizationUnitId);
  }, [props.organizationUnitId]);

  return (
    <Context.Provider
      value={{
        eva,
        organizationUnitId: props.organizationUnitId,
        organizationUnitBackendId: props.organizationUnitBackendId,
        configuration: props.configuration,
        algoliaIndexId,
      }}
    >
      {props.children}
    </Context.Provider>
  );
}

/**
 * NOTE eva instance do not consider user status,
 * use `hooks.useCallService` for user dependant calls
 */
export function useConfiguration() {
  const context = useContext(Context);

  if (!context) {
    throw new Error(`useConfiguration must be used within a ConfigurationProvider`);
  }

  return context;
}

export function useObjectConfigurationValue<T extends keyof KIKOApplicationConfiguration>(key: T) {
  const { configuration } = useConfiguration();
  const value = configuration?.[key];

  try {
    if (typeof value === "string") {
      return JSON.parse(value) as KIKOApplicationConfiguration[T];
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
}
